import React, { useState, useCallback, useEffect } from 'react';
import {
    X,
    Search,
    ChevronRight,
    AlertCircle,
    Layout,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { bigqueryApi } from '../../api/bigqueryApi';

const BillingProjectModal = ({ isOpen, onClose, onProjectSelected, currentBillingProject }) => {
    const [projects, setProjects] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const loadProjects = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await bigqueryApi.getProjects();
            setProjects(data);
        } catch (err) {
            console.error('Failed to load projects:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadProjects();
        }
    }, [isOpen, loadProjects]);

    const handleProjectSelect = async (project) => {
        try {
            setIsLoading(true);
            await bigqueryApi.setBillingProject(project.project_id);
            onProjectSelected && onProjectSelected(project);
            onClose();
        } catch (err) {
            console.error('Failed to set billing project:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredProjects = projects.filter(p =>
        p.project_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.friendly_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-950 w-full max-w-lg rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="p-8 border-b border-gray-50 dark:border-gray-900/50 flex items-center justify-between shrink-0">
                    <div>
                        <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">
                            Manage Connection
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                            Select Billing Project
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar min-h-[300px]">
                    <div className="space-y-3">
                        <div className="relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                            <input
                                type="text"
                                placeholder="Filter projects..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                            />
                        </div>

                        <div className="mb-4 flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 animate-in slide-in-from-top-2 duration-300">
                            <AlertCircle size={16} className="text-blue-600 shrink-0 mt-0.5" />
                            <div className="space-y-1">
                                <p className="text-[10px] font-black text-blue-900 dark:text-blue-100 uppercase tracking-tight">
                                    {currentBillingProject ? "Change Billing Project" : "Final Step Required"}
                                </p>
                                <p className="text-[9px] text-blue-700 dark:text-blue-300 font-medium leading-relaxed">
                                    Please choose your billing project. This project will be charged for any BigQuery data processing and query costs.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-1 pt-2">
                            {isLoading ? (
                                <div className="py-12 flex flex-col items-center justify-center gap-4">
                                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fetching projects...</span>
                                </div>
                            ) : filteredProjects.length === 0 ? (
                                <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No projects found</div>
                            ) : filteredProjects.map((project) => (
                                <button
                                    key={project.project_id}
                                    onClick={() => handleProjectSelect(project)}
                                    className={cn(
                                        "w-full flex items-center justify-between p-4 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/20 group transition-all",
                                        currentBillingProject === project.project_id && "bg-blue-50/50 dark:bg-blue-900/10 border-blue-100/50"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-blue-600 shadow-sm">
                                            <Layout size={16} />
                                        </div>
                                        <div className="text-left">
                                            <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight">{project.friendly_name}</div>
                                            <div className="text-[10px] text-gray-400 font-bold">{project.project_id}</div>
                                        </div>
                                    </div>
                                    {currentBillingProject === project.project_id ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[8px] font-black text-blue-600 uppercase tracking-widest">Current</span>
                                            <ChevronRight size={16} className="text-blue-500" />
                                        </div>
                                    ) : (
                                        <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillingProjectModal;
